import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  Alert,
  FormControlLabel,
  Checkbox,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableHead,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { colors } from '../../styles/theme/colors';
import { DecimalNumberInput } from '../../components/ui';
import type { SafetyStockRow } from '../../api/services/SafetyStockService';
import { SafetyStockService } from '../../api/services/SafetyStockService';
import { dialogZoomPaperSx } from '../../components/ui/DialogZoomTransition';
import { CloseConfirmationModal } from '../../components/modals/confirmation';

export interface BulkUpdateSelectedSafetyStockModalProps {
  open: boolean;
  onClose: () => void;
  selectedRows: SafetyStockRow[];
  onSuccess: (updatedCount?: number) => void;
}

const BulkUpdateSelectedSafetyStockModal: React.FC<BulkUpdateSelectedSafetyStockModalProps> = ({
  open,
  onClose,
  selectedRows,
  onSuccess,
}) => {
  const [safetyStock, setSafetyStock] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);

  useEffect(() => {
    if (open) {
      setSafetyStock('');
      setError(null);
      setConfirmed(false);
      setShowCloseConfirmation(false);
    }
  }, [open, selectedRows]);

  const hasChanges = useMemo(
    () => safetyStock.trim() !== '' || confirmed,
    [confirmed, safetyStock],
  );

  const doClose = useCallback(() => {
    setSafetyStock('');
    setError(null);
    setConfirmed(false);
    setShowCloseConfirmation(false);
    onClose();
  }, [onClose]);

  const handleClose = useCallback(() => {
    if (loading) return;
    if (hasChanges) {
      setShowCloseConfirmation(true);
      return;
    }
    doClose();
  }, [doClose, hasChanges, loading]);

  const handleConfirmClose = useCallback(() => {
    doClose();
  }, [doClose]);

  const handleCancelClose = useCallback(() => {
    setShowCloseConfirmation(false);
  }, []);

  const handleSave = async () => {
    const value = parseInt(safetyStock, 10);
    if (isNaN(value) || value < 0) {
      setError('El stock de seguridad debe ser un número entero mayor o igual a 0.');
      return;
    }
    if (selectedRows.length === 0) {
      setError('No hay productos seleccionados.');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const res = await SafetyStockService.productLocationSafetyStockControllerBulkUpdateSelected({
        safetyStock: value,
        items: selectedRows.map((row) => ({
          productId: row.productId,
          locationId: row.locationId,
        })),
      });
      onSuccess(res?.updated ?? 0);
      doClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al actualizar el stock de seguridad.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: dialogZoomPaperSx({
            borderRadius: 2,
            overflow: 'visible',
            boxShadow: '0 20px 60px rgba(2, 6, 23, 0.18)',
            margin: 'auto',
            width: '100%',
            maxWidth: { xs: 'min(720px, calc(100% - 32px))', sm: '720px' },
            bgcolor: 'background.paper',
          }),
        }}
      >
        <DialogTitle
          sx={(theme) => ({
            padding: '14px 20px',
            borderBottom: `1px solid ${theme.palette.divider}`,
            background: `linear-gradient(to right, ${colors.info.main}, ${colors.success.main})`,
            color: '#ffffff',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontWeight: 600,
            fontSize: '1rem',
          })}
        >
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#ffffff', fontSize: '1rem' }}>
            Actualizar stock de seguridad ({selectedRows.length})
          </Typography>
          <IconButton
            onClick={handleClose}
            sx={{ color: '#ffffff', '&:hover': { color: alpha('#fff', 0.85) } }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={(theme) => ({
            padding: '48px 28px 28px',
            overflowY: 'auto',
            overflowX: 'auto',
            minHeight: 120,
            backgroundColor: theme.palette.background.paper,
          })}
        >
          {error && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: '8px', fontSize: '14px' }}>
              {error}
            </Alert>
          )}

          <Box
            sx={{
              mb: 2.5,
              mt: 1,
              maxHeight: 280,
              overflow: 'auto',
            }}
          >
            <Table
              size="small"
              stickyHeader
              sx={(theme) => ({
                borderCollapse: 'collapse',
                '& .MuiTableCell-root': {
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  fontSize: '0.8125rem',
                  lineHeight: 1.35,
                },
                '& .MuiTableCell-root:first-of-type': {
                  pl: 0,
                },
                '& .MuiTableCell-root:last-of-type': {
                  pr: 0,
                },
              })}
            >
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={(theme) => ({
                      fontWeight: 700,
                      py: 1,
                      px: 1.25,
                      bgcolor: theme.palette.background.paper,
                    })}
                  >
                    Producto
                  </TableCell>
                  <TableCell
                    sx={(theme) => ({
                      fontWeight: 700,
                      py: 1,
                      px: 1.25,
                      bgcolor: theme.palette.background.paper,
                    })}
                  >
                    Código
                  </TableCell>
                  <TableCell
                    sx={(theme) => ({
                      fontWeight: 700,
                      py: 1,
                      px: 1.25,
                      bgcolor: theme.palette.background.paper,
                    })}
                  >
                    Ubicación
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={(theme) => ({
                      fontWeight: 700,
                      py: 1,
                      px: 1.25,
                      bgcolor: theme.palette.background.paper,
                    })}
                  >
                    Stock actual
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {selectedRows.map((row) => (
                  <TableRow key={`${row.productId}-${row.locationId}`} hover>
                    <TableCell sx={{ py: 0.875, px: 1.25 }}>{row.productName}</TableCell>
                    <TableCell sx={{ py: 0.875, px: 1.25 }}>{row.productCode}</TableCell>
                    <TableCell sx={{ py: 0.875, px: 1.25 }}>{row.sucursalName}</TableCell>
                    <TableCell align="right" sx={{ py: 0.875, px: 1.25 }}>
                      {row.stockSeguridad != null ? row.stockSeguridad : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <DecimalNumberInput
              fullWidth
              variant="outlined"
              size="medium"
              label="Nuevo valor de stock de seguridad *"
              value={safetyStock}
              onChange={(val) => setSafetyStock(val != null ? String(Math.trunc(val)) : '')}
              placeholder="0"
              decimalPlaces={0}
              min={0}
              disabled={loading}
              InputLabelProps={{ shrink: true }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={confirmed}
                  onChange={(e) => setConfirmed(e.target.checked)}
                  disabled={loading}
                  sx={{
                    color: colors.success.main,
                    '&.Mui-checked': { color: colors.success.main },
                  }}
                />
              }
              label={
                <Typography variant="body2" sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                  Confirmar actualización
                </Typography>
              }
              sx={{ marginLeft: 0 }}
            />
          </Box>
        </DialogContent>

        <DialogActions
          sx={(theme) => ({
            padding: '16px 24px',
            borderTop: `1px solid ${theme.palette.divider}`,
            backgroundColor:
              theme.palette.mode === 'dark'
                ? theme.palette.background.paper
                : theme.palette.grey[50],
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'flex-end',
            gap: 1.5,
          })}
        >
          <Button
            variant="tonal"
            onClick={handleClose}
            disabled={loading}
            sx={{
              minWidth: '120px',
              padding: '9px 18px',
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '14px',
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={loading || safetyStock === '' || !confirmed || selectedRows.length === 0}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
            sx={{
              textTransform: 'none',
              background: `linear-gradient(to right, ${colors.info.main}, ${colors.success.main})`,
              '&:disabled': { background: colors.grey[400], color: '#ffffff' },
            }}
          >
            {loading ? 'Actualizando...' : 'Actualizar'}
          </Button>
        </DialogActions>
      </Dialog>

      <CloseConfirmationModal
        open={showCloseConfirmation}
        onClose={handleCancelClose}
        onConfirm={handleConfirmClose}
      />
    </>
  );
};

export default BulkUpdateSelectedSafetyStockModal;
